(() => {
  'use strict';
  // The shared asset may be included by more than one section. Keep one editor listener.
  const owner = document.documentElement;
  if (owner.hasAttribute('data-lp-nightwear-pdp-runtime')) return;
  owner.setAttribute('data-lp-nightwear-pdp-runtime', '');
  const instances = new Map();
  const buyers = new Map();
  const linkedBuyer = node => {
    const target = node.dataset.lpnTarget;
    const product = node.dataset.lpnProduct;
    return [...buyers.entries()].find(([root]) => root.isConnected &&
      (!target || root.dataset.lpnSection === target) &&
      (!product || root.dataset.lpnProduct === product))?.[1];
  };
  const linkedNodes = (root, selector) => [...document.querySelectorAll(selector)]
    .filter(node => linkedBuyer(node) === buyers.get(root));
  const showBar = (bar, visible) => {
    bar.classList.toggle('show', visible);
    bar.setAttribute('aria-hidden', String(!visible));
    bar.inert = !visible;
  };
  function refreshLinks() {
    document.querySelectorAll('[data-lpn-sticky]').forEach(bar => {
      if (!linkedBuyer(bar)) {
        showBar(bar, false);
        bar.querySelector('[data-lpn-choose]').disabled = true;
      }
    });
    document.querySelectorAll('[data-lpn-closing]').forEach(section => {
      if (!linkedBuyer(section)) section.querySelector('[data-lpn-offer-button]').disabled = true;
    });
    buyers.forEach(buyer => buyer.refresh());
  }
  const text = (node, value) => { if (node) node.textContent = value == null ? '' : value; };
  const template = (value, fields) => Object.entries(fields).reduce(
    (result, [key, replacement]) => result.split(`[${key}]`).join(String(replacement)), value || '');
  // Liquid money filters can include markup. Decode their output without injecting HTML.
  const plainMoney = value => {
    const element = document.createElement('template');
    element.innerHTML = value || '';
    return [...element.content.childNodes].map(node => node.textContent).join('');
  };

  function mount(root) {
    if (instances.has(root)) return;
    const source = root.querySelector('script[type="application/json"]');
    if (!source) return;
    let data;
    try { data = JSON.parse(source.textContent); }
    catch (error) { console.error('lp-nightwear-pdp: invalid section data', error); return; }
    const controller = new AbortController();
    const { signal } = controller;
    const listeners = [];
    const on = (target, event, callback, options = {}) => {
      if (!target) return;
      target.addEventListener(event, callback, { ...options, signal });
      listeners.push(() => target.removeEventListener(event, callback, options));
    };
    const $ = role => root.querySelector(`[data-lpn-role="${role}"]`);
    const all = selector => [...root.querySelectorAll(selector)];
    const labels = data.labels || {};
    const GALLERY = data.GALLERY || [];
    let SIZES = data.SIZES || [];
    const SWATCH = data.SWATCH || {};
    const INFO = data.INFO || {};
    const variants = data.variants || [];
    const tiers = data.tiers || [];
    let DEFAULT_COLOURS = data.DEFAULT_COLOURS || [];
    // A bundle product carries one variant and no size or color option of its own. The
    // bundle app's selects ask for each piece instead, and their values ride to the cart as
    // line item properties, exactly as the app's own form would send them.
    const BUNDLE = Boolean(data.BUNDLE);
    const bundleSelects = () => [...root.querySelectorAll('[data-lpn-role="bundleOptions"] select')];
    // An app that supplied pickers must have every one of them answered. Without an app
    // block there is nothing to answer, and the single variant stands on its own.
    const bundleReady = () => bundleSelects().every(select => Boolean(select.value));
    const bundleProperties = () => Object.fromEntries(bundleSelects()
      .map(select => [select.name.replace(/^properties\[(.*)\]$/, '$1'), select.value]));
    // One size and one color for the whole set. Each of the app's selects carries its own
    // piece's options, named "<piece> - <size> / <color>", so a shared pick is read back out
    // of them: every piece must have that exact size and color, and have it in stock.
    const PIECE = /\s-\s(.+?)\s\/\s(.+)$/;
    const setOptions = () => bundleSelects().map(select => {
      const map = new Map();
      [...select.options].forEach(option => {
        const parts = PIECE.exec(option.value);
        if (!parts) return;
        map.set(`${parts[1]}\u241f${parts[2]}`, {
          value: option.value,
          stock: Number(option.dataset.inventory || 0) > 0 && !option.disabled
        });
      });
      return { select, map };
    });
    const setAxes = () => {
      const sizes = [];
      const colors = [];
      setOptions().forEach(({ map }) => [...map.keys()].forEach(key => {
        const [size, color] = key.split('\u241f');
        if (!sizes.includes(size)) sizes.push(size);
        if (!colors.includes(color)) colors.push(color);
      }));
      return { sizes, colors };
    };
    // A combination only exists if every piece of the set offers it and has it in stock.
    const setLive = (size, color) => {
      const pieces = setOptions();
      return pieces.length > 0 && pieces.every(({ map }) => map.get(`${size}\u241f${color}`)?.stock);
    };
    if (BUNDLE && data.BUNDLE_APP) {
      const axes = setAxes();
      if (axes.sizes.length && axes.colors.length) {
        SIZES = axes.sizes;
        DEFAULT_COLOURS = axes.colors;
      }
    }
    // One row is one whole set, so its size and color name both of its pieces.
    const setPropertiesFor = unit => Object.fromEntries(setOptions().map(({ select, map }) => [
      select.name.replace(/^properties\[(.*)\]$/, '$1'),
      map.get(`${unit?.size}\u241f${unit?.colour}`)?.value || ''
    ]));
    const selected = variants.find(v => v.id === data.selectedVariant) || variants.find(v => v.available) || variants[0];
    if (data.addon) data.addon.baseImage = root.querySelector('[data-lpn-role="addonImg"]')?.getAttribute('src') || null;
    let tier = tiers[0];
    let qty = tier?.quantity || 1;
    let lastUnit = 0;
    let editing = -1;
    let activeShot = 0;
    let busy = false;
    let purchaseStatus = '';
    let shotTimer;
    // A programmatic jump owns the track: snapping is turned off for it and the debounced
    // scroll listener stands down, so one click produces one move instead of a re-entrant second.
    let jumping = false;
    let jumpTimer;
    let addedTimer;
    let activeModal = null;
    let lastFocus = null;
    let previousOverflow = '';
    // Every offer keeps its own garments. Moving between offers must never carry a size or a
    // colour across: a shopper who opens "Buy Two" has chosen nothing there yet, and a
    // pre-filled row reads as already done and collapses before they have picked anything.
    const blankUnits = count => Array.from({ length: count }, () => ({ size: null, colour: null }));
    const unitsByTier = new Map();
    let units = blankUnits(qty);
    if (tier) unitsByTier.set(tier.id, units);
    const variantFor = unit => BUNDLE
      ? (data.BUNDLE_APP
          ? (unit?.size && unit.colour && setLive(unit.size, unit.colour) ? selected : null)
          : selected)
      : unit && variants.find(v =>
      (data.sizeIndex < 0 || v.options[data.sizeIndex] === unit.size) &&
      (data.colorIndex < 0 || v.options[data.colorIndex] === unit.colour));
    const colorVariant = unit => variantFor(unit)
      || (BUNDLE ? selected : variants.find(v => v.options[data.colorIndex] === unit?.colour));
    const chosen = n => Array.from({ length: n }, (_, i) => variantFor(units[i]));
    const done = unit => BUNDLE && !data.BUNDLE_APP
      ? Boolean(selected?.available) && bundleReady()
      : Boolean(unit?.size && unit.colour && variantFor(unit)?.available);
    // Either variant can be picked first, so each side filters the other. With a colour
    // chosen a size is live only in that colour, and with nothing chosen it is live if any
    // colour has it. Colours read the same way against the chosen size.
    const sizeLive = (size, colour) => colour
      ? Boolean(variantFor({ size, colour })?.available)
      : BUNDLE && data.BUNDLE_APP
        ? DEFAULT_COLOURS.some(c => setLive(size, c))
        : variants.some(v => v.available && v.options[data.sizeIndex] === size);
    const colourLive = (colour, size) => size
      ? Boolean(variantFor({ size, colour })?.available)
      : BUNDLE && data.BUNDLE_APP
        ? SIZES.some(s => setLive(s, colour))
        : variants.some(v => v.available && v.options[data.colorIndex] === colour);
    const openIndex = () => editing >= 0 ? editing : units.findIndex(unit => !done(unit));
    function setTier(offer) {
      if (!offer) return;
      tier = offer;
      qty = offer.quantity;
      if (!unitsByTier.has(offer.id)) unitsByTier.set(offer.id, blankUnits(qty));
      units = unitsByTier.get(offer.id);
      editing = -1;
      lastUnit = 0;
      render();
    }

    // Cheapest garment gets the discount. On ties, the last garment wins.
    function discountIndex(list = chosen(qty)) {
      let index = 0;
      list.forEach((variant, i) => {
        if (variant && (!list[index] || variant.price <= list[index].price)) index = i;
      });
      return index;
    }
    function priceFor(offer, list) {
      if (!offer || !selected) return null;
      // Unchosen garments keep the existing quote; only real selections can reach the cart.
      const key = `${offer.id}:${list.map(v => (v || selected).price).sort((a, b) => a - b).join(':')}`;
      return data.prices[key] || null;
    }
    function amounts(price) {
      return Object.fromEntries(Object.entries(price).map(([key, value]) =>
        [key, key.endsWith('Money') ? plainMoney(value) : value]));
    }

    function markShot(index) {
      if (!GALLERY.length) return;
      activeShot = Math.max(0, Math.min(index, GALLERY.length - 1));
      const shots = all('.hero-shot');
      // Preload the selected shot and its neighbors, including the wrapped ends.
      [-1, 0, 1, 2].forEach(offset => {
        const shot = shots[(activeShot + offset + shots.length) % shots.length];
        const image = shot?.querySelector('img');
        if (image) image.loading = 'eager';
      });
      all('[data-lpn-role="thumbs"] button').forEach((button, i) => {
        button.setAttribute('aria-selected', String(i === activeShot));
      });
      all('[data-lpn-role="heroDots"] i').forEach((dot, i) => dot.classList.toggle('on', i === activeShot));
      const rail = $('thumbs');
      const selectedThumb = rail?.querySelector('[aria-selected="true"]');
      // Move only the rail on its own axis; never move its scrolling ancestors.
      if (selectedThumb && rail) {
        if (rail.scrollHeight > rail.clientHeight + 1)
          rail.scrollTop = Math.max(0, selectedThumb.offsetTop - (rail.clientHeight - selectedThumb.offsetHeight) / 2);
        if (rail.scrollWidth > rail.clientWidth + 1)
          rail.scrollLeft = Math.max(0, selectedThumb.offsetLeft - (rail.clientWidth - selectedThumb.offsetWidth) / 2);
      }
    }
    function showShot(index) {
      const track = $('heroTrack');
      if (!track || !GALLERY.length) return;
      const next = (index + GALLERY.length) % GALLERY.length;
      // Mark BEFORE the jump. markShot flips the target shot and its neighbours to eager, so the
      // photo we are about to land on is already fetching rather than starting once we arrive.
      markShot(next);
      jumping = true;
      clearTimeout(jumpTimer);
      // A mandatory snap container re-runs its snap algorithm on a programmatic scrollLeft and can
      // ease across every shot in between, which is what read as lag. Turn snapping off for the
      // jump, move, then give it straight back so a finger and a wheel still snap normally.
      const snap = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';
      track.scrollLeft = next * track.clientWidth;
      track.style.scrollSnapType = snap;
      jumpTimer = setTimeout(() => { jumping = false; }, 200);
    }
    const colourMedia = unit => colorVariant(unit)?.mediaId
      || (unit?.colour ? data.COLOR_MEDIA?.[unit.colour] : null) || null;
    const colourImage = (unit, width) => {
      const variant = colorVariant(unit);
      if (variant?.[`image${width}`]) return variant[`image${width}`];
      const media = GALLERY.find(item => item.id === colourMedia(unit));
      return media ? media.src.replace(/([?&]width=)\d+/, `$1${width}`) : null;
    };
    function showInGallery(unit) {
      const index = GALLERY.findIndex(media => media.id === colourMedia(unit));
      if (index >= 0) showShot(index);
    }
    function sizeRail() {
      const rail = $('thumbs');
      const hero = root.querySelector('.hero-img');
      if (!rail || !hero) return;
      if (!window.matchMedia('(min-width:981px)').matches) {
        rail.style.height = '';
        rail.style.removeProperty('--rowH');
        return;
      }
      const height = hero.getBoundingClientRect().height;
      rail.style.height = `${height}px`;
      rail.style.setProperty('--rowH', `${Math.max(0, (height - 32) / 5)}px`);
    }

    // Build each offer's frame once. Selection changes only patch existing nodes.
    function buildRows() {
      all('.tier').forEach(card => {
        const offer = tiers.find(t => t.id === card.dataset.tier);
        const host = card.querySelector('.crows');
        if (!offer || !host || host.hasChildNodes()) return;
        for (let i = 0; i < offer.quantity; i++) {
          const garment = labels.garment || 'nightie';
          const label = offer.quantity === 1 ? `Your ${garment}`
            : `${['First', 'Second', 'Third'][i]} ${garment}`;
          const row = document.createElement('div');
          row.className = 'pmrow';
          const header = document.createElement('div');
          header.className = 'pmrow-hd';
          const number = document.createElement('span');
          number.className = 'pmrow-n';
          number.setAttribute('aria-hidden', 'true');
          const title = document.createElement('span');
          title.className = 'pmrow-t';
          title.textContent = label;
          const summary = document.createElement('span');
          summary.className = 'pmrow-pick';
          const edit = document.createElement('button');
          edit.className = 'pmrow-edit';
          edit.type = 'button';
          edit.dataset.i = i;
          edit.textContent = 'Change';
          edit.setAttribute('aria-label', `Change ${label.toLowerCase()}`);
          header.append(number, title, summary, edit);
          const body = document.createElement('div');
          body.className = 'pmrow-body';
          body.id = `lpn-${root.dataset.lpnSection}-${offer.id}-${i}`;
          edit.setAttribute('aria-controls', body.id);
          const sizes = document.createElement('div');
          sizes.className = 'sizerow';
          SIZES.forEach(size => {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.i = i;
            button.dataset.size = size;
            button.textContent = size;
            button.setAttribute('aria-label', `${label}: size ${size}`);
            sizes.append(button);
          });
          const pick = document.createElement('div');
          pick.className = 'pmrow-c';
          const swatches = document.createElement('span');
          swatches.className = 'sw';
          DEFAULT_COLOURS.forEach(color => {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.i = i;
            button.dataset.color = color;
            button.title = color;
            const sample = document.createElement('i');
            sample.style.background = SWATCH[color] || 'transparent';
            button.append(sample);
            swatches.append(button);
          });
          const side = document.createElement('div');
          side.className = 'pmrow-side';
          const picture = document.createElement('img');
          picture.decoding = 'async';
          picture.loading = 'lazy';
          const caption = document.createElement('span');
          side.append(picture, caption);
          pick.append(swatches, side);
          body.append(pick, sizes);
          row.append(header, body);
          host.append(row);
        }
      });
    }
    function patchRows() {
      const card = all('.tier').find(node => node.dataset.tier === tier?.id);
      if (!card) return;
      const open = openIndex();
      const cheapest = discountIndex();
      card.querySelectorAll('.pmrow').forEach((row, i) => {
        const unit = units[i];
        const complete = done(unit);
        row.classList.toggle('done', complete);
        row.classList.toggle('open', i === open);
        row.classList.toggle('shut', !complete && i !== open);
        // A settled size steps back so the eye moves down to the colours, which stay at full
        // strength until they are chosen too.
        row.classList.toggle('sized', Boolean(unit?.size));
        text(row.querySelector('.pmrow-n'), complete ? '✓' : i + 1);
        const summary = row.querySelector('.pmrow-pick');
        summary.hidden = !complete;
        text(summary, complete ? `${unit.colour} / ${unit.size}` : '');
        const edit = row.querySelector('.pmrow-edit');
        edit.hidden = !complete;
        edit.setAttribute('aria-expanded', String(i === open));
        row.querySelector('.pmrow-body').hidden = i !== open;
        row.querySelectorAll('[data-size]').forEach(button => {
          const selectedSize = button.dataset.size === unit.size;
          button.classList.toggle('on', selectedSize);
          button.setAttribute('aria-pressed', String(selectedSize));
          button.disabled = !sizeLive(button.dataset.size, unit.colour);
          button.classList.toggle('pm-dead', button.disabled);
        });
        row.querySelectorAll('.sw button').forEach(button => {
          const colour = button.dataset.color;
          button.classList.toggle('on', colour === unit.colour);
          button.setAttribute('aria-pressed', String(colour === unit.colour));
          const soldOut = !colourLive(colour, unit.size);
          button.setAttribute('aria-disabled', String(soldOut));
          button.setAttribute('aria-label', `${i + 1}: ${colour}${soldOut
            ? `, sold out${unit.size ? ` in size ${unit.size}` : ''}` : ''}`);
        });
        const rowImage = colourImage(unit, 240);
        const side = row.querySelector('.pmrow-side');
        side.hidden = !unit.colour;
        const picture = side.querySelector('img');
        if (rowImage && picture.getAttribute('src') !== rowImage) picture.setAttribute('src', rowImage);
        picture.hidden = !rowImage;
        picture.alt = `${data.title}: ${unit.colour || ''}`;
        text(side.querySelector('span'), unit.colour);
      });
    }
    function buildSizes() {
      const size = units[lastUnit]?.size;
      // A bundle has no size option of its own, so the modal's rail is built from its pieces.
      const rail = $('sizeRows');
      if (rail && !rail.children.length) SIZES.forEach(value => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.size = value;
        button.textContent = value;
        rail.append(button);
      });
      all('[data-lpn-role="sizeRows"] [data-size]').forEach(button => {
        const selectedSize = button.dataset.size === size;
        button.classList.toggle('on', selectedSize);
        button.setAttribute('aria-pressed', String(selectedSize));
        button.disabled = !sizeLive(button.dataset.size, units[lastUnit]?.colour);
      });
      text($('mPick'), size ? template(labels.use_size_label, { size }) : 'Choose a size');
      if ($('mPick')) $('mPick').disabled = !size;
    }
    // ---- Cross style add on -------------------------------------------------
    // One partner garment, offered under the button. It stays locked until the first
    // garment above is fully chosen, nothing is pre-selected, and the button total only
    // moves once its colour and size are both picked. Every price comes from Liquid,
    // priced per tier against the store's real discount codes, because adding a garment
    // can move a Buy X Get Y discount onto the cheaper item.
    const addon = data.addon || null;
    let addonOpen = false;
    let addonColour = null;
    let addonSize = null;
    let addonSettled = false;
    let addonTimer;

    const addonOffer = () => (addon && tier ? addon.byTier?.[tier.id] : null) || null;
    const addonVariant = () => {
      if (!addon || !addonColour || !addonSize) return null;
      return addon.variants.find(v =>
        v.options[addon.colorIndex] === addonColour && v.options[addon.sizeIndex] === addonSize) || null;
    };
    const addonReady = () => Boolean(addonSettled && addonVariant()?.available);
    // Locked until at least one garment above is a real, complete choice.
    const addonUnlocked = () => units.some(unit => done(unit));
    const addonColourAvailable = name => addon.variants.some(v =>
      v.options[addon.colorIndex] === name && v.available);
    const addonSizeAvailable = name => addon.variants.some(v =>
      v.options[addon.sizeIndex] === name && v.available &&
      (!addonColour || v.options[addon.colorIndex] === addonColour));


    /* ------------------------------------------------------------------ *
     * Quantity layout: one garment at a time.
     *
     * Pick a colour and a size, add it, pick another colour, add that one.
     * Nothing is counted here: the cart is the only source of truth, so the
     * bar, the next price and the discount code are all worked out from what
     * the cart actually holds. Remove something in the drawer and all three
     * correct themselves, because none of them remember anything.
     * The offer restarts every three garments.
     * ------------------------------------------------------------------ */
    const qbOn = Boolean(root.querySelector('.ladder.pm-ladder-quantity'));
    const qbStep = tiers.length ? Math.max(...tiers.map(t => t.quantity || 1)) : 3;
    let qbColour = null;
    let qbSize = null;
    let qbBuilt = false;
    let qbInCart = 0;     // this product's garments already in the cart
    let qbAddonInCart = 0;
    let qbAdding = false;
    let qbJustAdded = false;
    let qbAddedTimer;

    const qbVariantIds = new Set(variants.map(v => v.id));
    const qbAddonIds = new Set((addon?.variants || []).map(v => v.id));

    // what the whole run of garments costs, restarting the offer every qbStep
    function qbOrderTotal(n) {
      if (n <= 0 || !tiers.length) return 0;
      const full = tiers.find(t => t.quantity === qbStep);
      const rest = n % qbStep;
      const sets = Math.floor(n / qbStep);
      const restTier = rest ? tiers.find(t => t.quantity === rest) : null;
      const priceOf = offer => {
        const quote = priceFor(offer, Array.from({ length: offer.quantity }, () => selected));
        return quote ? quote.now : (selected?.price || 0) * offer.quantity;
      };
      return sets * (full ? priceOf(full) : 0) + (restTier ? priceOf(restTier) : 0);
    }
    const qbUnitCost = () => {
      const one = tiers.find(t => t.quantity === 1);
      const quote = one ? priceFor(one, [selected]) : null;
      return quote ? quote.now : (selected?.price || 0);
    };
    const qbNextCost = () => qbInCart >= qbStep
      ? qbUnitCost()
      : qbOrderTotal(qbInCart + 1) - qbOrderTotal(qbInCart);

    // Prices arrive from Liquid already formatted for the shopper's market, so an
    // amount worked out here is printed by swapping the number inside one of them.
    // That keeps the currency symbol and anything after it, whatever the market.
    function qbMoney(cents) {
      const one = tiers.find(t => t.quantity === 1);
      const quote = one ? priceFor(one, [selected]) : null;
      const sample = plainMoney(quote ? quote.nowMoney : '');
      const amount = (cents / 100).toFixed(2);
      if (!sample) return amount;
      return sample.replace(/\d[\d.,]*/, amount);
    }

    // the tier whose discount matches what is in the cart right now
    function qbTierForCart() {
      const within = qbInCart >= qbStep ? qbStep : qbInCart % qbStep;
      return tiers.find(t => t.quantity === within) || null;
    }

    async function qbReadCart() {
      if (!qbOn) return;
      try {
        const cart = await (await fetch(`${cartRoot()}cart.js`)).json();
        qbInCart = cart.items.reduce((sum, line) =>
          sum + (qbVariantIds.has(line.variant_id) ? line.quantity : 0), 0);
        qbAddonInCart = cart.items.reduce((sum, line) =>
          sum + (qbAddonIds.has(line.variant_id) ? line.quantity : 0), 0);
        // The cart can change anywhere, including the drawer, so the discount has to
        // follow it. Remove the third garment and the three for two code has to come
        // off, or the shopper pays full price for two. It is only written when it
        // actually differs, so this can never chase its own tail.
        const wanted = [
          qbTierForCart()?.code,
          qbAddonInCart ? addonOffer()?.code : ''
        ].filter(Boolean).filter((code, i, list) => list.indexOf(code) === i);
        const onCart = (cart.discount_codes || []).map(entry => entry.code).filter(Boolean);
        const same = wanted.length === onCart.length
          && wanted.every(code => onCart.includes(code));
        if (!same) await applyCodes(wanted);
      } catch (error) {
        // an unreadable cart just leaves the bar where it was
      }
      render();
    }

    function qbFill() {
      if (qbColour && qbSize && !variantFor({ size: qbSize, colour: qbColour })?.available) qbSize = null;
      // each add is a single garment, so the ladder always sits on its first offer
      if (tier?.quantity !== 1) { const one = tiers.find(t => t.quantity === 1); if (one) setTier(one); }
      units.forEach(unit => { unit.colour = qbColour; unit.size = qbSize; });
      editing = -1;
    }

    function qbLabel() {
      if (qbAdding) return labels.adding_label || 'Adding';
      if (qbJustAdded) return labels.added_label || 'Added to your cart';
      if (!qbColour) return labels.qb_pick_colour || 'Choose a color';
      if (!qbSize) return labels.qb_pick_size || 'Choose a size';
      const lead = qbInCart
        ? (labels.qb_again || 'Add another')
        : (labels.qb_first || 'Add to cart');
      return `${lead} \u00a0\u00b7\u00a0 ${qbMoney(qbNextCost())}`;
    }

    function qbProgressMarkup() {
      // without a 3rd-piece tier the offer ends at the 2nd piece, so the bar has one mark at its end
      const two = qbStep <= 2;
      return '<div class="pm-track">'
        + '<span class="pm-fill" data-qb="fill"></span>'
        + `<span class="pm-mark" data-qb="m2" style="left:${two ? 'calc(100% - 7px)' : '50%'}"></span>`
        + (two ? '' : '<span class="pm-mark" data-qb="m3" style="left:calc(100% - 7px)"></span>')
        + '</div>'
        + `<div class="pm-labels"${two ? ' style="justify-content:flex-end"' : ''}>`
        + `<span class="pm-lab" data-qb="l2"><span class="pm-tick">✓</span>${labels.qb_second || '2nd 50% off'}</span>`
        + (two ? '' : `<span class="pm-lab" data-qb="l3"><span class="pm-tick">✓</span>${labels.qb_third || '3rd free'}</span>`)
        + '</div>';
    }

    function qbBuild() {
      if (!qbOn || qbBuilt) return;
      const ladder = root.querySelector('.ladder');
      const cta = $('cta');
      if (!ladder || !cta) return;
      qbBuilt = true;

      const wrap = document.createElement('div');
      wrap.className = 'pm-qb';
      wrap.innerHTML =
        `<div class="step-hd"><span class="step-n"><b>${labels.qb_colour || 'Color'}</b> <em data-qb="colourName"></em></span></div>`
        + '<div class="sw" data-qb="colours" role="group"></div>'
        + `<div class="step-hd"><span class="step-n"><b>${labels.qb_size || 'Size'}</b> <em data-qb="sizeName"></em></span><span data-qb="findslot"></span></div>`
        + '<div class="sizerow" data-qb="sizes" role="group"></div>'
        + '<div class="pm-buyrow"></div>'
        + `<button class="pm-viewcart is-empty" type="button" data-qb-view>${labels.qb_view || 'View cart'} (0)</button>`
        + `<div class="pm-prog" data-qb-prog>${qbProgressMarkup()}</div>`;
      ladder.parentNode.insertBefore(wrap, ladder);
      wrap.querySelector('.pm-buyrow').appendChild(cta);

      const findSize = $('findSize');
      if (findSize) wrap.querySelector('[data-qb="findslot"]').appendChild(findSize);

      const colourHost = wrap.querySelector('[data-qb="colours"]');
      DEFAULT_COLOURS.forEach(name => {
        const button = document.createElement('button');
        button.type = 'button';
        button.title = name;
        button.dataset.qbColour = name;
        button.setAttribute('aria-label', name);
        const chip = document.createElement('i');
        chip.style.background = SWATCH[name] || 'transparent';
        button.appendChild(chip);
        colourHost.appendChild(button);
      });

      const sizeHost = wrap.querySelector('[data-qb="sizes"]');
      SIZES.forEach(name => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.qbSize = name;
        button.textContent = name;
        sizeHost.appendChild(button);
      });

      qbBuildBars();
      qbReadCart();
    }

    function qbBuildBars() {
      const bars = [$('sticky'), ...linkedNodes(root, '[data-lpn-sticky]')].filter(Boolean);
      bars.forEach(bar => {
        if (bar.querySelector('[data-qb-prog]')) return;
        const barCta = bar.querySelector('.cta');
        if (!barCta) return;
        const prog = document.createElement('div');
        prog.className = 'pm-sticky-prog';
        prog.innerHTML = `<div class="pm-prog" data-qb-prog>${qbProgressMarkup()}</div>`;
        bar.insertBefore(prog, barCta);
        const view = document.createElement('button');
        view.type = 'button';
        view.className = 'pm-viewcart is-empty';
        view.setAttribute('data-qb-view', '');
        view.textContent = `${labels.qb_view || 'View cart'} (0)`;
        bar.appendChild(view);
      });
    }

    // the cart is the shopper's own, so opening it is the theme's job
    function qbOpenCart() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') { drawer.show(); return; }
      window.location.href = `${cartRoot()}cart`;
    }

    function qbPaintProgress(host, count) {
      // the offer restarts every qbStep, so a full bar holds until the next one starts
      const within = count >= qbStep ? qbStep : count % qbStep;
      const fill = host.querySelector('[data-qb="fill"]');
      if (fill) fill.style.width = (within === 0 ? 0 : qbStep <= 2 ? within * 50 : within === 1 ? 25 : within === 2 ? 50 : 100) + '%';
      [['m2', 2], ['m3', 3], ['l2', 2], ['l3', 3]].forEach(([key, at]) => {
        const node = host.querySelector(`[data-qb="${key}"]`);
        if (node) node.classList.toggle('on', within >= at);
      });
      const second = host.querySelector('[data-qb="l2"]');
      const third = host.querySelector('[data-qb="l3"]');
      if (second) second.classList.toggle('next', within < 2);
      if (third) third.classList.toggle('next', within === 2);
    }

    function qbRender() {
      if (!qbOn || !qbBuilt) return;
      const wrap = root.querySelector('.pm-qb');
      if (!wrap) return;
      qbBuildBars();

      const colourLabel = wrap.querySelector('[data-qb="colourName"]');
      const sizeLabel = wrap.querySelector('[data-qb="sizeName"]');
      if (colourLabel) colourLabel.textContent = qbColour || (labels.qb_choose || 'Choose one');
      if (sizeLabel) sizeLabel.textContent = qbSize || (labels.qb_choose || 'Choose one');

      all('[data-qb-colour]').forEach(button => {
        const name = button.dataset.qbColour;
        button.classList.toggle('on', name === qbColour);
        button.setAttribute('aria-disabled', String(!colourLive(name, qbSize)));
      });
      all('[data-qb-size]').forEach(button => {
        const name = button.dataset.qbSize;
        button.classList.toggle('on', name === qbSize);
        button.classList.toggle('pm-dead', !sizeLive(name, qbColour));
      });

      // the bars sit outside this section's root, so they are collected by hand
      const inCart = qbInCart + qbAddonInCart;
      const bars = [$('sticky'), ...linkedNodes(root, '[data-lpn-sticky]')].filter(Boolean);
      const views = [...all('[data-qb-view]')];
      const progs = [...all('[data-qb-prog]')];
      bars.forEach(bar => {
        bar.querySelectorAll('[data-qb-view]').forEach(node => { if (!views.includes(node)) views.push(node); });
        bar.querySelectorAll('[data-qb-prog]').forEach(node => { if (!progs.includes(node)) progs.push(node); });
      });
      views.forEach(button => {
        button.classList.toggle('is-empty', inCart === 0);
        button.textContent = `${labels.qb_view || 'View cart'} (${inCart})`;
      });
      progs.forEach(host => qbPaintProgress(host, qbInCart));
    }

    // The theme opens its cart drawer, and updates its cart counters, off its own
    // cart:change event. Calling cartDrawer.open() does nothing, because this theme's
    // drawer has no such method, which is why an add used to give no sign at all.
    const cartRoot = () => window.Shopify?.routes?.root || '/';

    function bundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true, detail: { sections }
      }));
      return sections.join(',');
    }

    // Asking for the drawer's section markup in the add keeps the drawer in step with
    // the cart it is about to show.
    async function postToCart(items) {
      const response = await fetch(`${cartRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: bundledSections(), sections_url: location.pathname })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || labels.error_label);
      }
      return response.json();
    }

    async function applyCodes(codes) {
      const wanted = codes.filter(Boolean).filter((code, i, list) => list.indexOf(code) === i);
      if (!wanted.length) return;
      try {
        await fetch(`${cartRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: wanted.join(',') })
        });
      } catch (error) {
        // A failed code must never block the add. The cart is still correct, just undiscounted.
      }
    }

    // open = true is what makes the drawer slide out. Removing a line refreshes the
    // same drawer without throwing it open again.
    async function announceCart(added, open) {
      try {
        const cart = await (await fetch(`${cartRoot()}cart.js`)).json();
        if (added && added.sections) cart.sections = added.sections;
        if (open) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
          }));
        }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) {
        console.error('lp-nightwear-pdp: cart refresh failed', error);
      }
    }

    // The add on goes into the cart on its own, the moment a colour and a size are
    // picked, so the main button never adds it a second time.
    let addonInCart = null;
    let addonBusy = false;

    async function addonAddToCart() {
      const variant = addonVariant();
      if (!variant || !variant.available || addonBusy) return;
      if (addonInCart && addonInCart.id === variant.id) return;
      addonBusy = true;
      render();
      try {
        if (addonInCart) await addonRemoveFromCart(true);
        const added = await postToCart([{ id: variant.id, quantity: 1 }]);
        addonInCart = { id: variant.id, key: added?.items?.[0]?.key || added?.key || null };
        if (qbOn) await qbReadCart();
        await applyCodes([(qbOn ? qbTierForCart()?.code : tier?.code), addonOffer()?.code]);
        await announceCart(added, true);
      } catch (error) {
        addonInCart = null;
        addonSettled = false;
        const message = error.message || labels.error_label;
        const node = $('cartError');
        if (node) { text(node, message); node.hidden = false; }
      } finally {
        addonBusy = false;
        render();
      }
    }

    async function addonRemoveFromCart(quiet) {
      if (!addonInCart) return;
      const line = addonInCart.key || String(addonInCart.id);
      addonInCart = null;
      try {
        await fetch(`${cartRoot()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line, quantity: 0 })
        });
        if (!quiet) await announceCart(null, false);
      } catch (error) {
        console.error('lp-nightwear-pdp: could not take the add on back out', error);
      }
    }

    function addonTotalMoney(price) {
      const offer = addonOffer();
      if (addonInCart) return price.nowMoney;
      if (!addonReady() || !offer) return price.nowMoney;
      return plainMoney(offer.totalMoney) || price.nowMoney;
    }

    function addonReset() {
      clearTimeout(addonTimer);
      addonColour = null;
      addonSize = null;
      addonSettled = false;
    }

    function buildAddonChoices() {
      const colourHost = $('addonColors');
      const sizeHost = $('addonSizes');
      if (!addon || !colourHost || !sizeHost || colourHost.childElementCount) return;
      addon.colors.forEach(name => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pm-addon-sw';
        chip.dataset.addonColor = name;
        chip.title = name;
        chip.setAttribute('aria-label', name);
        // The partner's own hexes come from the block, so a chip is never a photo of a garment
        // sitting in a row of flat colours.
        const hex = addon.swatch?.[name] || SWATCH[name];
        if (hex) chip.style.background = hex;
        else {
          const shot = addon.variants.find(v => v.options[addon.colorIndex] === name && v.image);
          if (shot) { chip.style.backgroundImage = `url(${shot.image})`; chip.classList.add('is-photo'); }
        }
        if (!addonColourAvailable(name)) chip.classList.add('is-out');
        colourHost.append(chip);
      });
      addon.sizes.forEach(name => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pm-addon-sz';
        button.dataset.addonSize = name;
        button.textContent = name;
        sizeHost.append(button);
      });
    }

    function renderAddon() {
      const host = $('addon');
      if (!host || !addon) return;
      const offer = addonOffer();
      const unlocked = addonUnlocked();
      if (!unlocked && (addonOpen || addonSettled)) { addonOpen = false; addonReset(); }
      buildAddonChoices();
      host.classList.toggle('is-locked', !unlocked);
      host.classList.toggle('is-on', addonOpen);
      host.classList.toggle('is-done', addonSettled);
      const toggle = $('addonToggle');
      if (toggle) {
        toggle.setAttribute('aria-expanded', String(addonOpen && !addonSettled));
        toggle.setAttribute('aria-disabled', String(!unlocked));
      }
      // Price follows the tier, because the real cart total does.
      if (offer) {
        text($('addonNow'), plainMoney(offer.costMoney));
        const was = $('addonWas');
        if (was) {
          text(was, plainMoney(addon.wasMoney));
          was.hidden = offer.save <= 0;
        }
      }
      const stepColour = $('addonStepColor');
      const stepSize = $('addonStepSize');
      if (stepColour) stepColour.classList.toggle('is-ok', Boolean(addonColour));
      if (stepSize) {
        stepSize.classList.toggle('is-wait', !addonColour);
        stepSize.classList.toggle('is-ok', Boolean(addonSize));
      }
      all('[data-addon-color]').forEach(chip => {
        chip.classList.toggle('is-on', chip.dataset.addonColor === addonColour);
      });
      all('[data-addon-size]').forEach(button => {
        button.classList.toggle('is-on', button.dataset.addonSize === addonSize);
        button.classList.toggle('is-out', !addonSizeAvailable(button.dataset.addonSize));
      });
      const shot = $('addonImg');
      const chosenVariant = addon.variants.find(v => v.options[addon.colorIndex] === addonColour && v.image);
      const source = addonColour && chosenVariant ? chosenVariant.image : addon.baseImage;
      if (shot && source && shot.getAttribute('src') !== source) shot.setAttribute('src', source);
      const sub = $('addonSub');
      if (sub) {
        if (addonSettled) {
          sub.textContent = '';
          const line = document.createElement('span');
          line.textContent = addonInCart
            ? `${addon.inCartLabel || 'Added to your cart'}. ${addonColour}, ${addonSize}`
            : `${addonColour}, ${addonSize}`;
          const edit = document.createElement('span');
          edit.className = 'pm-addon-edit';
          edit.dataset.addonEdit = '';
          edit.textContent = addon.editLabel || 'Edit';
          sub.append(line, document.createElement('br'), edit);
        } else if (!unlocked) {
          text(sub, addon.lockedLabel || '');
        } else if (offer && offer.save > 0) {
          text(sub, template(addon.saveLabel || '', { price: plainMoney(offer.saveMoney) }));
        } else {
          text(sub, addon.addLabel || '');
        }
      }
    }

    function settleAddon() {
      if (!addonColour || !addonSize) return;
      clearTimeout(addonTimer);
      addonTimer = setTimeout(() => { addonSettled = true; render(); addonAddToCart(); }, 420);
    }

    function render() {
      qbRender();
      all('.tier').forEach(card => {
        const offer = tiers.find(t => t.id === card.dataset.tier);
        if (!offer) return;
        const active = offer.id === tier?.id;
        card.classList.toggle('on', active);
        card.querySelector('.tier-head').setAttribute('aria-expanded', String(active));
        const raw = priceFor(offer, chosen(offer.quantity));
        if (!raw) return;
        const price = amounts(raw);
        text(card.querySelector('[data-now]'), price.nowMoney);
        text(card.querySelector('[data-was]'), price.fullMoney);
        text(card.querySelector('[data-each]'), template(labels.each_label, { price: price.eachMoney }));
        text(card.querySelector('[data-save]'), template(labels.save_label, { price: price.saveMoney }));
        card.querySelector('[data-was]').hidden = !price.save;
        card.querySelector('[data-save]').hidden = !price.save;
      });
      buildSizes();
      patchRows();
      renderAddon();
      const list = chosen(qty);
      const raw = priceFor(tier, list);
      const canBuy = Boolean(tier && raw && units.length === qty && units.every(done));
      if ($('cta')) $('cta').disabled = qbOn ? (qbAdding || !qbColour || !qbSize) : (busy || !canBuy);
      if (qbOn) {
        text($('cta'), qbLabel());
        if ($('cta')) $('cta').classList.toggle('pm-added', qbJustAdded);
      } else if (!canBuy && !busy) {
        text($('cta'), units.some(unit => !unit.size) ? 'Choose a size' : 'Choose a colour');
      }
      if (!raw) { syncLinkedContent(null); syncBar(); return; }
      const price = amounts(raw);
      text($('headPrice'), price.nowMoney);
      const first = list[0] || selected;
      const compare = qty === 1 && first.compare > first.price ? plainMoney(first.compareMoney) : '';
      text($('headWas'), price.save ? price.fullMoney : compare);
      if ($('headWas')) $('headWas').hidden = !price.save && !compare;
      text($('perNight'), template(data.costText, { price: price.nightMoney }));
      if (!busy && canBuy && !qbOn) {
        text($('cta'), `${template(labels.cta_label, { quantity: qty })} \u00a0·\u00a0 ${addonTotalMoney(price)}`);
      }
      text($('bnplAmt'), price.installmentMoney);
      text($('stickyT'), tier.title);
      text($('stickyP'), qty > 1 ? `${price.nowMoney} \u00a0·\u00a0 ${template(labels.each_label, { price: price.eachMoney })}` : price.nowMoney);
      const stickyCta = $('stickyCta');
      if (stickyCta && !busy) {
        text(stickyCta, canBuy
          ? `${template(labels.cta_label, { quantity: qty })} \u00a0·\u00a0 ${addonTotalMoney(amounts(raw))}`
          : labels.sticky_label);
      }
      const stickyImage = $('stickyImg');
      const stickySrc = colourImage(units[0], 140);
      if (stickyImage && stickySrc && stickyImage.getAttribute('src') !== stickySrc)
        stickyImage.setAttribute('src', stickySrc);
      syncLinkedContent(price);
      syncBar();
    }

    function syncLinkedContent(price) {
      linkedNodes(root, '[data-lpn-sticky]').forEach(bar => {
        const button = bar.querySelector('[data-lpn-choose]');
        button.disabled = false;
        const ready = Boolean(tier && units.length === qty && units.every(done));
        button.dataset.ready = ready ? '1' : '';
        if (qbOn) {
          text(button, qbLabel());
          button.disabled = qbAdding || !qbColour || !qbSize;
          button.classList.toggle('pm-added', qbJustAdded);
        } else if (button.dataset.addLabel && !busy) {
          text(button, ready
            ? `${template(button.dataset.addLabel, { quantity: qty })}${price ? ` \u00a0·\u00a0 ${price.nowMoney}` : ''}`
            : button.dataset.chooseLabel || button.textContent);
        }
        text(bar.querySelector('[data-lpn-sticky-title]'), qbOn
          ? data.title
          : `${data.title} · ${tier?.title || ''}`);
        text(bar.querySelector('[data-lpn-sticky-price]'), qbOn
          ? qbMoney(qbOrderTotal(Math.max(1, qbInCart)))
          : (price ? (qty > 1 ? `${price.nowMoney} · ${template(labels.each_label, { price: price.eachMoney })}` : price.nowMoney) : ''));
        const selection = units.map(unit => [unit.size, unit.colour].filter(Boolean).join(' / ')).filter(Boolean).join(', ');
        text(bar.querySelector('[data-lpn-selection]'), selection);
        const barSrc = colourImage(units[lastUnit], 140);
        const picture = bar.querySelector('.sticky-thumb img');
        if (picture) {
          if (barSrc && picture.getAttribute('src') !== barSrc)
            picture.setAttribute('src', barSrc);
          picture.alt = `${data.title}: ${selection}`;
        }
      });
      linkedNodes(root, '[data-lpn-closing]').forEach(section => {
        const button = section.querySelector('[data-lpn-offer-button]');
        button.disabled = false;
      });
    }

    async function addToCart() {
      const list = chosen(qty);
      if (busy || !tier || units.length !== qty || !units.every(done) || !priceFor(tier, list)) return;
      busy = true;
      purchaseStatus = labels.adding_label;
      const errorNode = $('cartError');
      if (errorNode) errorNode.hidden = true;
      linkedNodes(root, '[data-lpn-sticky]').forEach(bar => {
        const node = bar.querySelector('[data-lpn-linked-error]');
        if (node) node.hidden = true;
      });
      render();
      text($('cta'), purchaseStatus);
      try {
        // Use the client's existing add, count refresh, and cartDrawer.open flow.
        // Separate quantity-1 entries preserve each garment's discount eligibility.
        const added = await postToCart((() => {
          const items = BUNDLE && data.BUNDLE_APP
            ? units.map(unit => ({
                id: selected.id, quantity: 1, properties: setPropertiesFor(unit)
              }))
            : BUNDLE
              ? [{ id: selected.id, quantity: qty, properties: bundleProperties() }]
              : list.map(variant => ({ id: variant.id, quantity: 1 }));
          // The add on put itself in the cart when it was picked, so it is not added again.
          const extra = !addonInCart && addonReady() ? addonVariant() : null;
          if (extra) items.push({ id: extra.id, quantity: 1 });
          return items;
        })());
        if (signal.aborted) return;
        // Put this tier's discount code on the session so the cart charges what the page says.
        // Shopify has no cart API for codes, so the supported /discount/<code> route is used: it
        // sets the code and redirects. A code left over from another tier is harmless, because a
        // Buy X Get Y code simply does not apply until its quantity is in the cart.
        // The tier's code and the add on's code both have to reach the cart, because each one
        // discounts a different garment. The /discount/ route only ever holds ONE code, so the
        // cart's own discount field is used instead: comma separated, it keeps both. Whether
        // both then APPLY is a Shopify setting, not a theme one. Each code must combine with
        // product discounts, and the tier code must be scoped to this page's product only,
        // or it takes the cheapest line in the cart, which is the add on, and the two collide.
        if (qbOn) await qbReadCart();
        await applyCodes([
          (qbOn ? qbTierForCart()?.code : tier?.code),
          (addonInCart || addonReady()) ? addonOffer()?.code : ''
        ]);
        if (signal.aborted) return;
        addonOpen = false;
        // It is part of the order now, so it is no longer ours to take back out.
        addonInCart = null;
        addonReset();
        text($('cta'), labels.added_label);
        purchaseStatus = labels.added_label;
        render();
        addedTimer = setTimeout(() => { busy = false; purchaseStatus = ''; render(); }, 2000);
        if (qbOn) {
          qbJustAdded = true;
          render();
          clearTimeout(qbAddedTimer);
          qbAddedTimer = setTimeout(() => { qbJustAdded = false; render(); }, 1600);
        }
        // while a reward is still within reach the drawer stays shut, so they can
        // pick another colour without closing anything
        const earned = qbOn && qbInCart >= qbStep;
        announceCart(added, !qbOn || earned);
      } catch (error) {
        if (signal.aborted) return;
        let message = error.message || labels.error_label;
        if (/sold out|unavailable/i.test(message)) message = labels.unavailable_label;
        else if (/inventory/i.test(message)) message = labels.inventory_label;
        else if (!navigator.onLine) message = labels.offline_label;
        text(errorNode, message);
        if (errorNode) errorNode.hidden = false;
        // The linked bar can add to cart too, so a failure has to be visible from there.
        linkedNodes(root, '[data-lpn-sticky]').forEach(bar => {
          const node = bar.querySelector('[data-lpn-linked-error]');
          if (node) { text(node, message); node.hidden = false; }
        });
        busy = false;
        purchaseStatus = '';
        render();
      }
    }

    function openModal(modal, trigger) {
      if (!modal) return;
      if (activeModal) closeModal();
      activeModal = modal;
      lastFocus = trigger || document.activeElement;
      previousOverflow = document.body.style.overflow;
      modal.classList.add('open');
      document.body.style.overflow = 'hidden';
      modal.querySelector('.modal-panel').focus();
    }
    function closeModal() {
      if (!activeModal) return;
      activeModal.classList.remove('open');
      activeModal = null;
      document.body.style.overflow = previousOverflow;
      if (lastFocus?.isConnected) lastFocus.focus();
    }
    function syncBar() {
      const sticky = $('sticky');
      const standalone = linkedNodes(root, '[data-lpn-sticky]');
      const cta = $('cta');
      if (!cta) return;
      const height = window.innerHeight;
      const mobile = window.matchMedia('(max-width:980px)').matches;
      const rect = cta.getBoundingClientRect();
      const ctaGone = rect.bottom < 40 || rect.top > height;
      const cards = all('.tier');
      const inOffers = cards.some(card => {
        const rect = card.getBoundingClientRect();
        return rect.bottom > 70 && rect.top < height * 0.75;
      });
      const visible = ctaGone && !(mobile && inOffers);
      if (sticky) {
        sticky.hidden = standalone.length > 0;
        showBar(sticky, visible && !standalone.length);
      }
      standalone.forEach((bar, index) => showBar(bar, visible && index === 0));
      root.classList.toggle('picking', mobile && inOffers);
    }
    const scrollPageTo = node => {
      if (node) window.scrollTo({ top: window.scrollY + node.getBoundingClientRect().top - 84, behavior: 'auto' });
      syncBar();
    };
    const chooseNighties = () => {
      // Land on the "How many" heading rather than inside the ladder, so the first tier is
      // never cut off above the fold, and leave room for the store's own sticky header.
      const box = (qbOn && root.querySelector('.pm-qb'))
        || root.querySelector('.pm-ladder-hd')
        || root.querySelector('.ladder');
      if (!box) return;
      window.scrollTo({ top: window.scrollY + box.getBoundingClientRect().top - 120, behavior: 'smooth' });
      if (qbOn) root.querySelector('[data-qb-colour]')?.focus({ preventScroll: true });
      else root.querySelector('.ladder')?.focus({ preventScroll: true });
    };
    const finishChoice = index => {
      lastUnit = index;
      if (done(units[index])) editing = -1;
      const focusedRow = document.activeElement?.closest('.pmrow-body');
      render();
      // A completed row hides its controls; keep keyboard focus in the buying flow.
      if (focusedRow?.hidden) {
        const next = root.querySelector('.tier.on .pmrow.open .sw button:not([aria-disabled="true"])')
          || root.querySelector('.tier.on .pmrow.open .sizerow button:not(:disabled)');
        (next || root.querySelector('.tier.on .pmrow-edit:not([hidden])'))?.focus({ preventScroll: true });
      }
    };
    on(root, 'click', event => {
      const target = event.target.closest('button, [data-close], [data-addon-edit]');
      if (!target) return;
      if (target.matches('[data-close]')) { closeModal(); return; }
      if (target.matches('[data-addon-edit]')) {
        addonRemoveFromCart();
        addonReset();
        addonOpen = true;
        render();
        return;
      }
      if (target.matches('[data-lpn-role="addonToggle"]')) {
        if (!addonUnlocked()) return;
        if (addonBusy) return;
        if (addonSettled) { addonRemoveFromCart(); addonOpen = false; addonReset(); }
        else { addonOpen = !addonOpen; if (!addonOpen) addonReset(); }
        render();
        return;
      }
      if (target.dataset.qbColour != null) {
        if (target.getAttribute('aria-disabled') === 'true') return;
        qbColour = target.dataset.qbColour;
        qbFill();
        render();
        return;
      }
      if (target.dataset.qbSize != null) {
        if (target.classList.contains('pm-dead')) return;
        qbSize = target.dataset.qbSize;
        qbFill();
        render();
        return;
      }
      if (target.hasAttribute('data-qb-view')) {
        qbOpenCart();
        return;
      }
      if (target.dataset.addonColor != null) {
        if (target.classList.contains('is-out')) return;
        addonColour = target.dataset.addonColor;
        if (addonSize && !addonSizeAvailable(addonSize)) addonSize = null;
        settleAddon();
        render();
        return;
      }
      if (target.dataset.addonSize != null) {
        if (target.classList.contains('is-out')) return;
        addonSize = target.dataset.addonSize;
        settleAddon();
        render();
        return;
      }
      if (target.dataset.info) {
        const info = INFO[target.dataset.info];
        if (info) {
          text($('infoTitle'), info.t);
          text($('infoBody'), info.b);
          openModal($('infoModal'), target);
        }
        return;
      }
      if (target.dataset.size) {
        const index = target.dataset.i == null ? lastUnit : Number(target.dataset.i);
        const unit = units[index];
        if (!unit || !sizeLive(target.dataset.size, unit.colour)) return;
        unit.size = target.dataset.size;
        finishChoice(index);
        return;
      }
      if (target.matches('.pmrow-edit')) {
        const index = Number(target.dataset.i);
        const reopening = editing !== index;
        editing = reopening ? index : -1;
        // Changing a finished garment starts it over, so the size and the colour are both
        // chosen again and the pair on screen is always the pair going into the cart.
        if (reopening && units[index]) { units[index].size = null; units[index].colour = null; }
        lastUnit = index;
        render();
        return;
      }
      if (target.matches('.sw button')) {
        if (target.getAttribute('aria-disabled') === 'true') {
          // Sold out in this size: still show the garment, but never make it the unit we would
          // add to cart, so the swatch keeps its sold out look and checkout stays honest.
          showInGallery({ size: units[Number(target.dataset.i)].size, colour: target.dataset.color });
          return;
        }
        const index = Number(target.dataset.i);
        units[index].colour = target.dataset.color;
        // Colour first is allowed, so a size that this colour is not stocked in has to go.
        if (units[index].size && !variantFor(units[index])?.available) units[index].size = null;
        showInGallery(units[index]);
        finishChoice(index);
        return;
      }
      if (target.matches('.tier-head')) {
        setTier(tiers.find(t => t.id === target.closest('.tier').dataset.tier));
      }
    });
    on($('findSize'), 'click', event => {
      lastUnit = openIndex() >= 0 ? openIndex() : lastUnit;
      buildSizes();
      openModal($('sizeModal'), event.currentTarget);
    });
    on($('mPick'), 'click', closeModal);
    on(document, 'keydown', event => {
      if (!activeModal) return;
      if (event.key === 'Escape') { event.preventDefault(); closeModal(); }
      if (event.key !== 'Tab') return;
      const focusable = [...activeModal.querySelectorAll('button:not(:disabled), a[href], input, select, [tabindex="0"]')];
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === activeModal.querySelector('.modal-panel'))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    });
    on($('thumbs'), 'click', event => {
      const button = event.target.closest('button[data-shot]');
      if (button) showShot(Number(button.dataset.shot));
    });
    const step = direction => {
      const track = $('heroTrack');
      const current = track?.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : activeShot;
      showShot(current + direction);
    };
    on($('heroPrev'), 'click', () => step(-1));
    on($('heroNext'), 'click', () => step(1));
    on($('heroTrack'), 'scroll', () => {
      if (jumping) return;
      clearTimeout(shotTimer);
      shotTimer = setTimeout(() => {
        if (jumping) return;
        const track = $('heroTrack');
        if (track.clientWidth) markShot(Math.round(track.scrollLeft / track.clientWidth));
      }, 90);
    }, { passive: true });
    on($('cta'), 'click', addToCart);
    on($('stickyCta'), 'click', () => {
      if (tier && units.length === qty && units.every(done)) addToCart(); else chooseNighties();
    });
    on($('stickyThumb'), 'click', () => scrollPageTo(root.querySelector('.hero-img')));
    on(window, 'scroll', syncBar, { passive: true });
    on(window, 'resize', () => { sizeRail(); showShot(activeShot); syncBar(); });
    on(window, 'load', sizeRail);
    const observer = new ResizeObserver(sizeRail);
    const hero = root.querySelector('.hero-img');
    if (hero) observer.observe(hero);
    buyers.set(root, {
      refresh: render,
      chooseNighties,
      addToCart,
      selectOffer: quantity => {
        const offer = tiers.find(t => t.quantity === quantity) || tier;
        if (offer && !qbOn) setTier(offer);
        chooseNighties();
      },
      back: () => scrollPageTo(root.querySelector('.hero-img'))
    });
    instances.set(root, () => {
      closeModal();
      controller.abort();
      listeners.forEach(remove => remove());
      observer.disconnect();
      clearTimeout(shotTimer);
      clearTimeout(addedTimer);
      clearTimeout(addonTimer);
      clearTimeout(jumpTimer);
      buyers.delete(root);
    });
    buildRows();
    qbBuild();
    if (qbOn) {
      document.addEventListener('cart:change', qbReadCart);
      document.documentElement.addEventListener('cart:change', qbReadCart);
      document.addEventListener('cart:refresh', qbReadCart);
      window.addEventListener('pageshow', event => { if (event.persisted) qbReadCart(); });
    }
    // The app owns its selects. Read them on change so the button follows the choice.
    if (BUNDLE) on(root, 'change', event => {
      if (event.target.closest('[data-lpn-role="bundleOptions"]')) render();
    });
    // Warm every colour's small row picture once. That picture swaps its src on each swatch
    // click, and an uncached swap blanks the frame before the new photo paints, which is the
    // flicker you see on a phone. Fetched once here, every later swap comes from cache.
    new Set(variants.map(v => v.image240).filter(Boolean)).forEach(src => {
      const warm = new Image();
      warm.decoding = 'async';
      warm.src = src;
    });
    sizeRail();
    // Keep the product's initial gallery shot without preselecting a garment.
    const startShot = () => {
      const index = GALLERY.findIndex(media => media.id === selected?.mediaId);
      if (index < 0) { markShot(0); return; }
      const track = $('heroTrack');
      // A track with no width yet cannot be scrolled, so mark it now and place it once laid out.
      if (track && track.clientWidth) showShot(index); else markShot(index);
    };
    startShot();
    // Layout can settle after this runs (web fonts, the sticky column), so place it once more.
    window.addEventListener('load', startShot, { once: true });
    render();
    mountSupportingContent(root, data, on, () => {
      const offer = tiers.find(t => t.quantity === 3);
      if (offer && !qbOn) setTier(offer);
      chooseNighties();
    });
  }

  // Later native sections can supply their own JSON and scoped markup. No review,
  // press, quotation or size-chart data is embedded in this shared asset.
  function mountSupportingContent(root, data, on, selectThree) {
    const find = role => root.querySelector(`[data-lpn-role="${role}"]`);
    const REVIEWS = data.REVIEWS || [];
    const STAR_BREAKDOWN = data.STAR_BREAKDOWN || [];
    const PRESS = data.PRESS || [];
    const QUOTES = data.QUOTES || [];
    const reviewLabels = data.reviewLabels || {};
    const list = find('arList');
    let shown = 4, filter = 'all', sort = 'recent';
    const stars = rating => {
      const span = document.createElement('div');
      span.className = 'stars';
      span.textContent = '★'.repeat(rating);
      const empty = document.createElement('span');
      empty.style.color = '#D8D8D8';
      empty.textContent = '★'.repeat(5 - rating);
      span.append(empty);
      return span;
    };
    function buildReviews() {
      if (!list) return;
      let reviews = REVIEWS.filter(r => filter === 'all' || (filter === 'low' ? r.s <= 3 : r.s === Number(filter)));
      if (sort === 'high') reviews.sort((a, b) => b.s - a.s);
      if (sort === 'low') reviews.sort((a, b) => a.s - b.s);
      const fragment = document.createDocumentFragment();
      reviews.slice(0, shown).forEach(review => {
        const item = document.createElement('div'); item.className = 'ar-item';
        const who = document.createElement('div'); who.className = 'ar-who';
        const name = document.createElement('b'); name.textContent = review.n;
        const date = document.createElement('small'); date.textContent = review.d;
        who.append(name, date);
        if (review.verified) {
          const verified = document.createElement('span'); verified.className = 'ar-ver';
          verified.textContent = reviewLabels.verified; who.append(verified);
        }
        const body = document.createElement('div'); body.className = 'ar-body'; body.append(stars(review.s));
        if (review.t) { const heading = document.createElement('h4'); heading.textContent = review.t; body.append(heading); }
        const copy = document.createElement('p'); copy.textContent = review.b; body.append(copy);
        item.append(who, body); fragment.append(item);
      });
      if (!reviews.length) { const empty = document.createElement('p'); empty.textContent = reviewLabels.empty; fragment.append(empty); }
      list.replaceChildren(fragment);
      const more = find('arMore');
      if (more) { more.disabled = shown >= reviews.length; more.hidden = reviews.length <= 4; text(more, more.disabled ? reviewLabels.done : reviewLabels.more); }
    }
    const bars = find('bars');
    if (bars) {
      const total = STAR_BREAKDOWN.reduce((sum, [, count]) => sum + count, 0);
      STAR_BREAKDOWN.forEach(([rating, count]) => {
        const row = document.createElement('div'); row.className = 'bar';
        const label = document.createElement('span'); label.textContent = `${rating} ★`; label.style.background = 'none'; label.style.height = 'auto';
        const track = document.createElement('span'); const bar = document.createElement('b'); bar.style.width = `${total ? count / total * 100 : 0}%`; track.append(bar);
        const value = document.createElement('em'); value.textContent = count; row.append(label, track, value); bars.append(row);
      });
    }
    on(find('arMore'), 'click', () => { shown = Math.min(shown + 4, REVIEWS.length); buildReviews(); });
    on(root.querySelector('.ar-tools'), 'click', event => {
      const chip = event.target.closest('.chip'); if (!chip) return;
      root.querySelectorAll('.chip').forEach(c => c.classList.toggle('on', c === chip));
      filter = chip.dataset.f; shown = 4; buildReviews();
    });
    on(root.querySelector('.ar-sort'), 'change', event => { const value = event.target.value; sort = value === 'Highest rating' ? 'high' : value === 'Lowest rating' ? 'low' : value; shown = 4; buildReviews(); });
    const loop = (track, records, make) => {
      if (!track || track.hasChildNodes()) return;
      for (let half = 0; half < 2; half++) {
        const group = document.createElement('div'); group.className = 'lpn-loop-half';
        if (half) group.setAttribute('aria-hidden', 'true');
        records.forEach(record => group.append(make(record))); track.append(group);
      }
    };
    loop(find('pressTrack'), PRESS, record => {
      const image = document.createElement('img'); image.src = record.src || record; image.alt = record.alt || ''; image.loading = 'lazy'; return image;
    });
    loop(find('marq'), QUOTES, ([quote, who]) => {
      const span = document.createElement('span'); span.textContent = `“${quote}” `;
      const name = document.createElement('em'); name.textContent = who; span.append(name); return span;
    });
    on(find('midBtn'), 'click', selectThree);
    buildReviews();
  }

  function scan(container) {
    if (container.matches?.('[data-lpn-section]')) mount(container);
    container.querySelectorAll('[data-lpn-section]').forEach(mount);
    refreshLinks();
  }
  // Supporting sections lead back to the mounted buyer to finish each garment.
  // Delegation also covers sections inserted or reordered in the theme editor.
  document.addEventListener('click', event => {
    const button = event.target.closest('button');
    if (button?.matches('[data-lpn-dialog-open]')) {
      const dialog = document.getElementById(button.dataset.lpnDialogOpen);
      if (dialog?.matches('.lpn .lpn-lab-dialog')) dialog.showModal();
      return;
    }
    if (button?.matches('[data-lpn-dialog-close]')) {
      button.closest('dialog')?.close();
      return;
    }
    if (event.target.matches('.lpn .lpn-lab-dialog')) {
      const dialog = event.target;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
    const section = button?.closest('[data-lpn-sticky], [data-lpn-closing]');
    const buyer = section && linkedBuyer(section);
    if (!buyer || button.disabled) return;
    if (button.matches('[data-lpn-offer-button]')) buyer.selectOffer(Number(button.dataset.quantity));
    else if (button.matches('[data-lpn-choose]')) {
      if (button.dataset.ready) buyer.addToCart(); else buyer.chooseNighties();
    }
    if (button.matches('[data-lpn-back]')) buyer.back();
  });
  document.addEventListener('shopify:section:load', event => {
    instances.forEach((dispose, root) => {
      if (!root.isConnected || event.target === root || event.target.contains(root)) {
        dispose();
        instances.delete(root);
      }
    });
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, root) => {
      if (event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
    });
    // Unload is dispatched before Shopify removes the section from the DOM.
    event.target.querySelectorAll('[data-lpn-sticky], [data-lpn-closing]').forEach(node => {
      if (node.hasAttribute('data-lpn-sticky')) showBar(node, false);
      node.removeAttribute('data-lpn-sticky');
      node.removeAttribute('data-lpn-closing');
    });
    refreshLinks();
  });
  document.addEventListener('shopify:section:reorder', refreshLinks);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
